import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { useBoolean, useInput } from "react-hanger";
import styled from "styled-components";
import Alert from "@mui/material/Alert";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import MenuIcon from "@mui/icons-material/Menu";

import { alertActions, authActions } from "../../redux/actions";
import { BACKEND_URL } from "../../constants";

const Branding = styled.div`
  font-family: "Bolts SF Regular";
  font-size: 24px;
  color: #ffffff;
`;

const Header = (props) => {
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
  const isChangePasswordDialogOpen = useBoolean(false);
  const currentPassword = useInput("");
  const newPassword = useInput("");
  const [newPasswordHelpText, setNewPasswordHelpText] = useState(null);
  const confirmNewPassword = useInput("");
  const [confirmNewPasswordHelpText, setConfirmNewPasswordHelpText] = useState(null);

  const handleMenuButtonClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  }

  const handleMenuOrders = () => {
    navigate("/orders");
    handleMenuClose();
  }

  const handleMenuCsReports = () => {
    navigate("/cs-reports");
    handleMenuClose();
  }

  const handleMenuTopTenTalentThemes = () => {
    navigate("/top-ten-talent-themes");
    handleMenuClose();
  }

  const handleMenuGuidanceDatabase = () => {
    navigate("/guidance-database");
    handleMenuClose();
  }

  const handleMenuCoupleGuidanceDatabase = () => {
    navigate("/couple-guidance-database");
    handleMenuClose();
  }

  const handleMenuThinkificDiscountCodes = () => {
    navigate("/thinkific-discount-codes");
    handleMenuClose();
  }

  const handleProfileButtonClick = (event) => {
    setProfileMenuAnchorEl(event.currentTarget);
  }

  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  }

  const handleChangePasswordDialogClose = () => {
    isChangePasswordDialogOpen.setFalse();
    props.alertReset();
    currentPassword.clear();
    newPassword.clear();
    confirmNewPassword.clear();
  }

  const handleChangePassword = () => {
    isChangePasswordDialogOpen.setTrue();
    handleProfileMenuClose();
  }

  const handleChangePasswordConfirm = () => {
    props.alertReset();
    props.changePassword(currentPassword.value, newPassword.value);
  }

  const handleLogout = () => {
    props.logout();
    handleProfileMenuClose();
  }

  useEffect(() => {
    if (newPassword.value.length > 0) {
      if (newPassword.value.length < 8)
        setNewPasswordHelpText("Password must be at least 8 characters");
      else if (!/[0-9]/.test(newPassword.value))
        setNewPasswordHelpText("Password must contain at least 1 numeric character");
      else if (!/[a-z]/.test(newPassword.value))
        setNewPasswordHelpText("Password must contain at least 1 lowercase character");
      else if (!/[A-Z]/.test(newPassword.value))
        setNewPasswordHelpText("Password must contain at least 1 uppercase character");
      else if (!/[^0-9a-zA-Z]/.test(newPassword.value))
        setNewPasswordHelpText("Password must contain at least 1 special character");
      else
        setNewPasswordHelpText(null);
    } else {
      setNewPasswordHelpText(null);
    }
  }, [newPassword]);

  useEffect(() => {
    if ((newPassword.value.length > 0) && (confirmNewPassword.value.length > 0)) {
      if (confirmNewPassword.value !== newPassword.value)
        setConfirmNewPasswordHelpText("Password does not match");
      else
        setConfirmNewPasswordHelpText(null);
    } else {
      setConfirmNewPasswordHelpText(null);
    }
  }, [newPassword, confirmNewPassword])

  useEffect(() => {
    props.alertReset();
    props.getUser();
  }, []);

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          height: "80px",
          backgroundColor: "#dc0100",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Branding>HONG BAO MEDIA</Branding>
          <Toolbar>
            <IconButton
              id="menu-button"
              size="large"
              color="secondary"
              aria-controls="menu"
              aria-haspopup="true"
              aria-expanded={Boolean(menuAnchorEl) ? "true" : undefined}
              onClick={handleMenuButtonClick}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu"
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              MenuListProps={{
                "aria-labelledby": "menu-button",
              }}
            >
              <MenuItem onClick={handleMenuOrders}>Orders</MenuItem>
              <Divider />
              <MenuItem onClick={handleMenuCsReports}>CS Reports</MenuItem>
              <MenuItem onClick={handleMenuTopTenTalentThemes}>Top 10 Talent Themes</MenuItem>
              <Divider />
              <MenuItem onClick={handleMenuGuidanceDatabase}>Guidance Database</MenuItem>
              <MenuItem onClick={handleMenuCoupleGuidanceDatabase}>HRC Guidance Database</MenuItem>
              <Divider />
              <MenuItem onClick={handleMenuThinkificDiscountCodes}>Thinkific Discount Codes</MenuItem>
            </Menu>
          </Toolbar>

          {props.user &&
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                id="profile"
                variant="text"
                color="secondary"
                aria-controls="profile-menu"
                aria-haspopup="true"
                aria-expanded={Boolean(profileMenuAnchorEl) ? "true" : undefined}
                onClick={handleProfileButtonClick}
              >
                <Avatar
                  alt={props.user.first_name || props.user.username}
                  src={`${BACKEND_URL}${props.user.profile.photo}`}
                  sx={{ marginRight: "8px" }}
                />
                {props.user.first_name || props.user.username}
              </Button>
              <Menu
                id="profile-menu"
                anchorEl={profileMenuAnchorEl}
                open={Boolean(profileMenuAnchorEl)}
                onClose={handleProfileMenuClose}
                MenuListProps={{
                  "aria-labelledby": "profile",
                }}
              >
                <MenuItem onClick={handleChangePassword}>Change password</MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </Box>
          }
        </Container>
      </AppBar>
      <Dialog
        open={isChangePasswordDialogOpen.value}
        onClose={handleChangePasswordDialogClose}
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {
            props.alert.message &&
            <Alert variant="outlined" severity={ props.alert.type }>
              { props.alert.message }
            </Alert>
          }
          <TextField
            fullWidth
            variant="outlined"
            type="password"
            label="Current password"
            value={ currentPassword.value }
            onChange={ currentPassword.onChange }
            disabled={ props.isChangingPassword }
            sx={{ marginTop: "12px" }}
          />
          <TextField
            fullWidth
            variant="outlined"
            type="password"
            label="New password"
            value={ newPassword.value }
            onChange={ newPassword.onChange }
            disabled={ props.isChangingPassword }
            error={ newPasswordHelpText !== null }
            helperText={ newPasswordHelpText }
            sx={{ marginTop: "12px" }}
          />
          <TextField
            fullWidth
            variant="outlined"
            type="password"
            label="Confirm new password"
            value={ confirmNewPassword.value }
            onChange={ confirmNewPassword.onChange }
            disabled={ props.isChangingPassword }
            error={ confirmNewPasswordHelpText !== null }
            helperText={ confirmNewPasswordHelpText }
            sx={{ marginTop: "12px" }}
          />
          <DialogActions>
            <Button
              variant="contained"
              disabled={
                !currentPassword.value
                || !newPassword.value
                || newPasswordHelpText !== null
                || !confirmNewPassword.value
                || confirmNewPasswordHelpText !== null
                || props.isChangingPassword
              }
              onClick={handleChangePasswordConfirm}
              sx={{ mt: 1, mr: 1 }}
            >
              Change password
              {props.isChangingPassword &&
                <>
                  <span>&nbsp;</span>
                  <CircularProgress color="primary" size={16} />
                </>
              }
            </Button>
            <Button
              variant="outlined"
              onClick={handleChangePasswordDialogClose}
              disabled={ props.isChangingPassword }
              sx={{ mt: 1, mr: 1 }}
            >
              Cancel
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
}

const mapState = (state) => {
  const {
    isGettingUser,
    user,
    isChangingPassword
  } = state.auth;
  return {
    alert: state.alert,
    isGettingUser,
    user,
    isChangingPassword,
  };
}

const actionCreators = {
  alertReset: alertActions.reset,
  getUser: authActions.getUser,
  logout: authActions.logout,
  changePassword: authActions.changePassword,
};

export default connect(mapState, actionCreators)(Header);